// =============================================================================
// design-system/components/DateTimeField — KANONİK tarih/saat picker (TEK SİSTEM).
//
// Takvim, inspector, modal ve tablolardaki TÜM tarih/saat alanları AYNI bu
// modülü kullanır. Kontroller artık NATIVE `<input type="date">` /
// `<input type="time">` — tarayıcının kendi picker'ı (vendor YOK; react-datepicker
// SÖKÜLDÜ, "zımbırtı" kalıntısı bırakılmadı). Token'lı, zarif, EventCompose çizgisi.
//
// Export'lar:
//   - DateField      : native date + native time (date+time alanı)
//   - DateRangeField : start/due ikilisi (iki DateField)
//   - TimeField      : BAĞIMSIZ native saat input'u
//   - Helper'lar     : normalizeTimeInput, extractDateInputValue,
//                      extractTimeInputValue, buildDateTimeValue,
//                      toPickerDateValue, TIME_OPTIONS, PopoverSurface
//
// Wiring imzaları (BİREBİR korundu):
//   <TimeField value="HH:mm" onChange={(next)=>…} />   // normalize "HH:mm" döner
//   <DateField dateValue timeValue onDateChange={(Date)=>…} onTimeChange={(HH:mm)=>…} />
// =============================================================================
import React from "react";

const padDatePart = (value) => String(value).padStart(2, "0");

// Saat seçici listesi — 30 dk aralıklı (00:00 … 23:30). Geriye-uyumluluk için
// export edilir (InspectorFieldControls re-export eder).
export const TIME_OPTIONS = (() => {
  const opts = [];
  for (let h = 0; h < 24; h += 1) {
    for (let m = 0; m < 60; m += 30) {
      opts.push(`${padDatePart(h)}:${padDatePart(m)}`);
    }
  }
  return opts;
})();

const toIsoDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return [
    date.getFullYear(),
    padDatePart(date.getMonth() + 1),
    padDatePart(date.getDate()),
  ].join("-");
};

const buildRelativeDate = (days) => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + days);
  return toIsoDate(date);
};

export const normalizeTimeInput = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${padDatePart(value.getHours())}:${padDatePart(value.getMinutes())}`;
  }
  const raw = String(value || "").trim();
  if (!raw) return "";
  const match = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return "";
  return `${padDatePart(match[1])}:${match[2]}`;
};

export const extractDateInputValue = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return raw.slice(0, 10);
  }

  const dayFirstMatch = raw.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})/);
  if (dayFirstMatch) {
    const [, day, month, year] = dayFirstMatch;
    return `${year}-${padDatePart(month)}-${padDatePart(day)}`;
  }

  const lower = raw.toLowerCase();
  if (lower.startsWith("today")) return buildRelativeDate(0);
  if (lower.startsWith("yesterday")) return buildRelativeDate(-1);
  if (lower.startsWith("tomorrow")) return buildRelativeDate(1);

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? "" : toIsoDate(parsed);
};

export const extractTimeInputValue = (value, fallback = "") => {
  const explicit = normalizeTimeInput(fallback);
  if (explicit) return explicit;

  const raw = String(value || "").trim();
  if (!raw) return "";

  const match = raw.match(/(\d{1,2}:\d{2})/);
  return match ? normalizeTimeInput(match[1]) : "";
};

export const buildDateTimeValue = (dateValue, timeValue) => {
  if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
    return "";
  }

  const day = padDatePart(dateValue.getDate());
  const month = padDatePart(dateValue.getMonth() + 1);
  const year = dateValue.getFullYear();
  const normalizedTime = normalizeTimeInput(timeValue);
  const dateText = `${day}-${month}-${year}`;
  return normalizedTime ? `${dateText} ${normalizedTime}` : dateText;
};

export const toPickerDateValue = (value) => {
  const normalizedDate = extractDateInputValue(value);
  if (!normalizedDate) return null;

  const [year, month, day] = normalizedDate.split("-").map(Number);
  const nextDate = new Date(year, (month || 1) - 1, day || 1);
  return Number.isNaN(nextDate.getTime()) ? null : nextDate;
};

// Geriye-uyumluluk: bazı paneller PopoverSurface'ı re-export ediyor.
export function PopoverSurface({ children, className = "" }) {
  return (
    <div className={`task-panel__popover-surface ${className}`.trim()}>
      {children}
    </div>
  );
}

// -----------------------------------------------------------------------------
// TimeField — NATIVE <input type="time">. Token'lı, zarif. Eski yazılabilir +
// 30-dk dropdown (custom portal) kaldırıldı — tarayıcı kendi picker'ını verir.
// -----------------------------------------------------------------------------
export function TimeField({ value, onChange, disabled = false }) {
  return (
    <input
      type="time"
      className="ds-datetime-input ds-datetime-input--time"
      value={normalizeTimeInput(value)}
      disabled={disabled}
      onChange={(event) => onChange?.(normalizeTimeInput(event.target.value))}
    />
  );
}

// -----------------------------------------------------------------------------
// DateField — NATIVE <input type="date"> + TimeField. Wiring birebir korundu:
// dateValue/timeValue/onDateChange(Date)/onTimeChange("HH:mm").
// -----------------------------------------------------------------------------
export function DateField({
  dateValue,
  timeValue,
  disabled = false,
  onDateChange,
  onTimeChange,
}) {
  const dateStr = extractDateInputValue(dateValue);
  const normalizedTime = extractTimeInputValue(dateValue, timeValue);

  return (
    <div className="ds-datetime-field">
      <input
        type="date"
        className="ds-datetime-input"
        value={dateStr}
        disabled={disabled}
        onChange={(event) => onDateChange?.(toPickerDateValue(event.target.value))}
      />
      <TimeField
        value={normalizedTime}
        disabled={disabled}
        onChange={(nextTime) => onTimeChange?.(normalizeTimeInput(nextTime))}
      />
    </div>
  );
}

// -----------------------------------------------------------------------------
// DateRangeField — start/due ikilisi (iki DateField). Inspector "Date range".
// -----------------------------------------------------------------------------
export function DateRangeField({
  startLabel = "Start",
  endLabel = "Due",
  startDateValue,
  startTimeValue,
  endDateValue,
  endTimeValue,
  disabled = false,
  onStartDateChange,
  onStartTimeChange,
  onEndDateChange,
  onEndTimeChange,
}) {
  return (
    <div className="ds-datetime-range">
      <div className="ds-datetime-range__item">
        <span className="ds-datetime-range__label">{startLabel}</span>
        <DateField
          dateValue={startDateValue}
          timeValue={startTimeValue}
          disabled={disabled}
          onDateChange={onStartDateChange}
          onTimeChange={onStartTimeChange}
        />
      </div>
      <div className="ds-datetime-range__item">
        <span className="ds-datetime-range__label">{endLabel}</span>
        <DateField
          dateValue={endDateValue}
          timeValue={endTimeValue}
          disabled={disabled}
          onDateChange={onEndDateChange}
          onTimeChange={onEndTimeChange}
        />
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// DatePicker — react-datepicker DROP-IN (aynı API: selected/onChange(Date)/
// placeholderText/dateFormat/isClearable/disabled/className). NATIVE render eder
// → "sistemde başka tarih/saat zımbırtısı yok". customInput YOK SAYILIR (native
// input zaten kendi picker'ını verir; tek görünüm `.ds-datetime-input`).
// dateFormat'a göre tip: "MMMM yyyy"→month, "yyyy"→year, diğer→date.
// -----------------------------------------------------------------------------
const monthInputValue = (value) => {
  const d = toPickerDateValue(value) || (value instanceof Date ? value : null);
  if (!d) return "";
  return `${d.getFullYear()}-${padDatePart(d.getMonth() + 1)}`;
};

export function DatePicker({
  selected,
  onChange,
  placeholderText,
  dateFormat = "",
  disabled = false,
  className = "",
  // react-datepicker-only prop'ları YUT (DOM'a sızmasın):
  isClearable,  
  customInput,  
  showMonthDropdown,  
  showYearDropdown,  
  dropdownMode,  
  todayButton,  
  popperPlacement,  
  popperClassName,  
  calendarClassName,  
  selectsRange,  
  startDate,  
  endDate,  
  minDate,  
  maxDate,  
  showTimeSelect,  
  ...rest
}) {
  const isMonth = /^M+\s*y+$/i.test(dateFormat.trim());
  const isYear = /^y+$/i.test(dateFormat.trim());
  const type = isMonth ? "month" : isYear ? "number" : "date";

  const value = isMonth
    ? monthInputValue(selected)
    : isYear
    ? (toPickerDateValue(selected)?.getFullYear() ?? "")
    : extractDateInputValue(selected);

  const handleChange = (event) => {
    const raw = event.target.value;
    if (!raw) {
      onChange?.(null);
      return;
    }
    if (isMonth) {
      const [y, m] = raw.split("-").map(Number);
      onChange?.(new Date(y, (m || 1) - 1, 1));
    } else if (isYear) {
      onChange?.(new Date(Number(raw), 0, 1));
    } else {
      onChange?.(toPickerDateValue(raw));
    }
  };

  return (
    <input
      {...rest}
      type={type}
      className={`ds-datetime-input ${className}`.trim()}
      value={value}
      disabled={disabled}
      title={placeholderText || undefined}
      aria-label={placeholderText || undefined}
      onChange={handleChange}
      {...(isYear ? { min: 1970, max: 2100, placeholder: placeholderText } : {})}
    />
  );
}

export default DateField;
